/**
 * Admin AI generation API client.
 *
 * Centralizes generation-page API calls using the shared apiUtils layer.
 *
 * @module adminGenerationApi
 */

import { apiGet, apiPost } from '@/lib/apiUtils';

const silentOptions = {
  suppressErrorToast: true,
} as const;

export interface SampleQuestionsResponse {
  readonly questions?: unknown[];
}

export interface GenerateQuestionsResponse {
  readonly contexts?: unknown[];
  readonly questions?: unknown[];
  readonly generation_info?: {
    readonly model?: string | null;
    readonly [key: string]: unknown;
  };
  readonly generationInfo?: {
    readonly model?: string | null;
    readonly [key: string]: unknown;
  };
}

export interface GenerateAudioResponse {
  readonly contexts: unknown[];
}

export interface SaveGeneratedContextResponse {
  readonly message?: string;
  readonly questionCount?: number;
  readonly error?: string;
  readonly details?: string;
}

export async function fetchSampleQuestions(params: {
  readonly examType: string;
  readonly level: string;
  readonly module: string;
  readonly section: string;
  readonly sampleCount: number;
}): Promise<SampleQuestionsResponse | null> {
  const query = new URLSearchParams({
    examType: params.examType,
    level: params.level,
    module: params.module,
    section: params.section,
    sampleCount: String(params.sampleCount),
  });

  return apiGet<SampleQuestionsResponse>(`/admin/sample-questions?${query.toString()}`, {
    context: 'fetchSampleQuestions',
    errorMessage: 'Error loading sample data',
    errorReturnValue: null,
    ...silentOptions,
  });
}

export async function generateQuestions(body: unknown): Promise<GenerateQuestionsResponse> {
  const result = await apiPost<GenerateQuestionsResponse>('/admin/generate-questions', body, {
    context: 'generateQuestions',
    errorMessage: 'Question generation failed',
    throwOnError: true,
    ...silentOptions,
  });
  if (!result) {
    throw new Error('Question generation failed');
  }
  return result;
}

export async function generateAudioForTranscripts(
  body: unknown,
): Promise<GenerateAudioResponse> {
  const result = await apiPost<GenerateAudioResponse>(
    '/admin/generate-audio-for-transcript',
    body,
    {
      context: 'generateAudioForTranscripts',
      errorMessage: 'Audio generation failed',
      throwOnError: true,
      ...silentOptions,
    },
  );
  if (!result) {
    throw new Error('Audio generation failed');
  }
  return result;
}

export async function saveGeneratedContext(body: {
  readonly contextData: unknown;
  readonly questions: unknown[];
  readonly generationModel?: string | null;
}): Promise<SaveGeneratedContextResponse> {
  const result = await apiPost<SaveGeneratedContextResponse>('/admin/save-generated-context', body, {
    context: 'saveGeneratedContext',
    errorMessage: 'Error saving context',
    throwOnError: true,
    ...silentOptions,
  });
  if (!result) {
    throw new Error('Error saving context');
  }
  return result;
}
